//
// Copyright 2020 DXOS.org
//

// NOTE: lit dev-mode warning suppression lives inline in `index.html` because
// ES module imports below this point are hoisted above any executable
// statement, so the same suppression here would run after lit's
// `queueMicrotask` warning has already fired.

import '@dxos-theme';

import * as Effect from 'effect/Effect';
import * as Match from 'effect/Match';
import React, { StrictMode, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { EdgeRegistryPluginProvider, type Plugin, PluginAssetCache, UrlLoader } from '@dxos/app-framework';
import { bootLoader, useApp } from '@dxos/app-framework/ui';
import { AppActivationEvents } from '@dxos/app-toolkit';
import { EdgeHttpClient } from '@dxos/edge-client';
import { EffectEx } from '@dxos/effect';
import { LogLevel, log } from '@dxos/log';
import { IdbLogStore } from '@dxos/log-store-idb';
import { Observability } from '@dxos/observability';
import { translations as observabilityTranslations } from '@dxos/plugin-observability/translations';
import { ErrorBoundary, ErrorFallback } from '@dxos/react-error-boundary';
import { ThemeProvider, Tooltip } from '@dxos/react-ui';
import { defaultTx } from '@dxos/react-ui';
import { TRACE_PROCESSOR } from '@dxos/tracing';
import { getHostPlatform, isMobile as isMobile$, isTauri as isTauri$ } from '@dxos/util';

import { ResetDialog } from './components';
import { type PluginConfig, getDefaults, getPlugins } from './plugin-defs';
import {
  APP_KEY,
  LOG_STORE_DB_NAME,
  PARAM_LOG_LEVEL,
  PARAM_PROFILER,
  PARAM_SAFE_MODE,
  defaultStorageIsEmpty,
  downloadLogs,
  initializeObservability,
  isFalse,
  isTrue,
  runStorageResetMigration,
  setSafeModeUrl,
  setupConfig,
  shouldRunStorageResetMigration,
  showDevRssBanner,
  startupProfiler,
  translations,
} from './util';

// Injected by the `define` block in vite.config.ts; '' in production builds.
declare const __DX_DEV_SERVER_BOOT_ID__: string;

declare global {
  interface ImportMeta {
    env: ImportMetaEnv;
  }

  interface ImportMetaEnv {
    DEV: string;
  }

  // Debug hook: run `downloadLogs()` from devtools to save buffered logs (same as Reset dialog).
  var downloadLogs: () => Promise<void>;
}

/**
 * Updates the native-DOM boot loader text. No-op once React has replaced #root.
 * The CSS animation in `index.html` keeps painting on the compositor thread
 * regardless of main-thread work, so this is purely textual feedback.
 */
const bootStatus = (text: string) => bootLoader?.status({ humanized: text });

// Stamp every (re-)evaluation of this module so we can tell Vite HMR reloads
// from a true page boot. Dev-only — production has no HMR and the diagnostic
// would just be noise. `import.meta.env.DEV` is statically replaced at build
// time, so the whole block tree-shakes out of prod bundles.
const BOOT_ID = import.meta.env?.DEV ? Math.random().toString(36).slice(2, 10) : '';
const MODULE_EVAL_TIME = Date.now();
if (import.meta.env?.DEV) {
  log('composer main: module evaluated', { bootId: BOOT_ID, t: MODULE_EVAL_TIME });
  const importMeta = import.meta as any;
  if (importMeta.hot) {
    importMeta.hot.dispose(() => {
      log('composer main: hmr dispose', { bootId: BOOT_ID, ageMs: Date.now() - MODULE_EVAL_TIME });
    });
  }
}

// Cross-tab reload coordination for persistent worker failures (dev only — see
// `onPersistentWorkerFailure` below). The sessionStorage guard makes each tab escalate at most
// once per tab session, so a failure that reloads don't fix cannot loop the reloads.
const DEV_RELOAD_CHANNEL = 'dxos-dev-worker-reload';
const DEV_RELOAD_GUARD_KEY = 'dxos.composer.dev-worker-reload';
if (import.meta.env?.DEV) {
  new BroadcastChannel(DEV_RELOAD_CHANNEL).onmessage = () => window.location.reload();
}

/**
 * Picks the platform-appropriate offline asset cache for third-party plugins.
 *  - Tauri (desktop + iOS): Rust-backed filesystem cache, served via the `dxos-plugin://` URI scheme.
 *  - Web with PWA enabled: cache managed by the SW in `./sw.ts`.
 *  - Otherwise (PWA disabled, tests, unsupported environments): a no-op cache;
 *    plugins still load but lose their offline guarantee.
 *
 * `isPwa` (not `'serviceWorker' in navigator`) is the SW gate because that
 * property is `true` in every modern browser whether or not a worker is
 * actually registered. On non-PWA builds (`DX_PWA=false` → `selfDestroying`
 * VitePWA, e.g. `labs.composer.space`) there is no active registration, and
 * the SW-backed cache's first call is `await navigator.serviceWorker.ready`
 * which resolves only when one exists — hangs forever otherwise, freezing
 * `Install` indefinitely with no timeout.
 *
 * Each branch dynamic-imports its impl so vite emits per-platform chunks instead
 * of dragging both into the initial bundle.
 */
const createAssetCache = async (isPwa: boolean, isTauri: boolean): Promise<PluginAssetCache.Cache> => {
  if (isTauri) {
    const { createTauriAssetCache } = await import('./asset-cache/tauri');
    return createTauriAssetCache();
  }
  if (isPwa && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    const { createServiceWorkerAssetCache } = await import('./asset-cache/service-worker');
    return createServiceWorkerAssetCache();
  }
  return PluginAssetCache.noop();
};

const main = async () => {
  if (import.meta.env?.DEV) {
    log('composer main: main() running', { bootId: BOOT_ID });
    // Fire-and-forget: surfaces the latest entry from a dev RSS feed under
    // the boot loader as a small distraction during cold boots. Routed
    // through the Vite dev server's `/api/rss` proxy (see vite.config.ts)
    // to dodge CORS. Tree-shakes out of prod via the `import.meta.env.DEV`
    // gate above.
    void showDevRssBanner();
  }

  const url = new URL(window.location.href);
  const safeMode = isTrue(url.searchParams.get(PARAM_SAFE_MODE), false);
  if (safeMode) {
    log.info('SAFE MODE');
    setSafeModeUrl(false);
  }

  // The startup profiler is on by default in dev so every devloop produces
  // a BENCHMARKS row without remembering `?profiler=1`. Production explicitly
  // opts in (or out) via the URL parameter.
  const profilerEnabled = isTrue(url.searchParams.get(PARAM_PROFILER), Boolean(import.meta.env?.DEV));
  const profiler = profilerEnabled ? startupProfiler() : undefined;

  const logLevel = url.searchParams.get(PARAM_LOG_LEVEL) ?? (safeMode ? 'debug' : undefined);
  if (logLevel) {
    const level = LogLevel[logLevel.toUpperCase() as keyof typeof LogLevel];
    log.config({ filter: level });
  }

  TRACE_PROCESSOR.setInstanceTag('app');

  const logStore = new IdbLogStore({ dbName: LOG_STORE_DB_NAME });
  log.addProcessor(logStore.processor);

  // Devtools convenience — also surfaced via the help panel and ResetDialog UI.
  globalThis.downloadLogs = () => downloadLogs(logStore);

  profiler?.mark('dynamic-imports:start');
  bootStatus('Loading framework…');

  // Load these in parallel; HTTP/2 multiplexes the four chunks and even on
  // local-disk the parser can interleave parses.
  const [{ Config, defs, SaveConfig }, { createClientServices }, { Migrations }, { __COMPOSER_MIGRATIONS__ }] =
    await Promise.all([
      import('@dxos/config'),
      import('@dxos/react-client'),
      import('@dxos/migrations'),
      import('./migrations'),
    ]);

  profiler?.mark('dynamic-imports:end');
  profiler?.measure('dynamic-imports', 'dynamic-imports:start', 'dynamic-imports:end');

  // Namespace for global Composer test & debug hooks.
  const otel = {
    /** Enable debug-level OTEL log export for this device (persisted across reloads, works in workers). */
    enableDebugLogs: () => {
      void Observability.storeOtelLogLevel(APP_KEY, 'debug');
      log.info('otel debug log level enabled — reload to apply');
    },
    /** Remove the debug override and revert to the default INFO log level. */
    disableDebugLogs: () => {
      void Observability.storeOtelLogLevel(APP_KEY, null);
      log.info('otel debug log level override removed — reload to apply');
    },
    /** Return the active OTEL log level override, or null if using the default. */
    getLogLevel: async (): Promise<string | null> => {
      const level = await Observability.getOtelLogLevel(APP_KEY);
      log.info('otel log level', { level: level ?? 'default (INFO)' });
      return level;
    },
  };
  (window as any).composer = { profiler, otel };

  Migrations.define(APP_KEY, __COMPOSER_MIGRATIONS__);

  profiler?.mark('config:start');
  bootStatus('Reading configuration…');

  let config = await setupConfig();
  if (shouldRunStorageResetMigration(config.values.runtime?.app?.env?.DX_ENVIRONMENT)) {
    await runStorageResetMigration();
    window.location.replace(window.location.href);
    return;
  }

  if (
    !config.values.runtime?.client?.storage?.dataStore &&
    (await defaultStorageIsEmpty(config.values.runtime?.client?.storage))
  ) {
    // NOTE: Set default for first time users to IDB (works better with automerge CRDTs).
    // Needs to be done before worker is created.
    await SaveConfig({
      runtime: {
        client: {
          storage: { dataStore: defs.Runtime.Client.Storage.StorageDriver.IDB },
        },
      },
    });
    config = await setupConfig();
  }

  profiler?.mark('config:end');
  profiler?.measure('config', 'config:start', 'config:end');

  const isTauri = isTauri$();
  if (isTauri) {
    const platform = getHostPlatform();
    document.body.setAttribute('data-platform', platform);
  }

  // Read the persisted opt-out state up front so we can suppress PostHog's heavy
  // instrumentation (session recorder, dead-clicks autocapture) at init time —
  // opting out after init only stops event capture, not script loading.
  const [observabilityDisabled, observabilityGroup] = await Promise.all([
    Observability.isObservabilityDisabled(APP_KEY),
    Observability.getObservabilityGroup(APP_KEY),
  ]);

  // Intentionally do not await; the buffering backend in TRACE_PROCESSOR captures
  // early spans and replays them once the real OTEL backend registers.
  const observability = initializeObservability(config, isTauri, logStore, observabilityDisabled);

  // Capture a one-shot `composer.startup` event when the framework dispatches
  // `app-framework:startup-activated`. Includes total ms, per-phase ms, top-5
  // slowest modules, transferred bytes (best-effort), and the boot-loader
  // visibility mark. Reads `performance.getEntriesByType` directly so production
  // builds without `?profiler=1` still get data.
  const captureStartupSummary = (): Record<string, string | number | boolean | undefined> => {
    const measures = performance.getEntriesByType('measure');
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const bootMark = performance.getEntriesByName('boot:html-parsed')[0];
    const firstInteractive = performance.getEntriesByName('app-framework:first-interactive')[0];
    const phaseDuration = (name: string): number =>
      Math.round(measures.find((measure) => measure.name === `startup:${name}`)?.duration ?? 0);
    const moduleEntries = measures
      .filter((measure) => measure.name.startsWith('module:'))
      .sort((first, second) => second.duration - first.duration);
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const transferredBytes = Math.round(resources.reduce((total, resource) => total + (resource.transferSize ?? 0), 0));
    // Flatten the top-5 modules into individual primitive keys (`top1Module`,
    // `top1Ms`, …) — observability `Attributes` only accept string | number |
    // boolean | undefined, and per-key fields are easier to filter on in
    // PostHog than a JSON blob.
    const summary: Record<string, string | number | boolean | undefined> = {
      totalMs: phaseDuration('total'),
      dynamicImportsMs: phaseDuration('dynamic-imports'),
      configMs: phaseDuration('config'),
      servicesMs: phaseDuration('services'),
      pluginsInitMs: phaseDuration('plugins-init'),
      bootLoaderVisibleMs: bootMark ? Math.round(bootMark.startTime) : undefined,
      firstInteractiveMs: firstInteractive ? Math.round(firstInteractive.startTime) : undefined,
      domContentLoadedMs: navigation ? Math.round(navigation.domContentLoadedEventEnd) : undefined,
      transferredBytes,
      moduleCount: moduleEntries.length,
    };
    moduleEntries.slice(0, 5).forEach((entry, index) => {
      summary[`top${index + 1}Module`] = entry.name.replace('module:', '');
      summary[`top${index + 1}Ms`] = Math.round(entry.duration);
    });
    return summary;
  };
  window.addEventListener(
    'app-framework:startup-activated',
    () => {
      const summary = captureStartupSummary();
      void observability
        .then((obs) => {
          obs.events.captureEvent('composer.startup', summary);
          log.info('startup', summary);
        })
        .catch((error) => log.catch(error));
    },
    { once: true },
  );
  // Detect if this is the popover window in Tauri.
  const isPopover = await Match.value(isTauri).pipe(
    Match.when(
      true,
      Effect.fnUntraced(function* () {
        const { getCurrentWindow } = yield* Effect.promise(() => import('@tauri-apps/api/window'));
        const tauriWindow = getCurrentWindow();
        return tauriWindow.label === 'popover';
      }),
    ),
    Match.when(false, () => Effect.succeed(false)),
    Match.exhaustive,
    EffectEx.runPromise,
  );

  // Detect mobile operating systems (phones only, not tablets).
  const isMobile = await Match.value(isTauri).pipe(
    Match.when(
      true,
      Effect.fnUntraced(function* () {
        const { type: osType } = yield* Effect.promise(() => import('@tauri-apps/plugin-os'));
        const platform = osType();
        return platform === 'android' || platform === 'ios';
      }),
    ),
    Match.when(false, () => Effect.sync(() => isTrue(config.values.runtime?.app?.env?.DX_MOBILE) || isMobile$())),
    Match.exhaustive,
    EffectEx.runPromise,
  );

  // Use in-process coordinator (no SharedWorker) for mobile Tauri apps only. iOS WKWebView has a
  // separate SharedWorker crash bug (Apple FB11723920) unrelated to origin. Desktop Tauri uses
  // tauri-plugin-localhost which serves from http://localhost, giving SharedWorker a proper origin.
  const useSingleClientMode = isTauri && isMobile;

  profiler?.mark('services:start');
  bootStatus('Starting services…');

  // Decide the deployment mode for client services. The factory is a dumb switch on
  // `runtime.client.services_mode` — the app is responsible for picking the right mode from its
  // env / platform constraints. Worker factories are passed unconditionally; the factory only
  // invokes the one required by the configured mode. Host mode (in-thread services) is opt-in via
  // DX_HOST; otherwise services run in a dedicated worker elected via a lock (leader/follower).
  const useLocalServices = isTrue(config.values.runtime?.app?.env?.DX_HOST);
  const servicesMode = useLocalServices
    ? defs.Runtime.Client.ServicesMode.HOST
    : defs.Runtime.Client.ServicesMode.DEDICATED_WORKER;

  config = new Config(
    {
      runtime: {
        client: {
          observabilityGroup,
          signalTelemetryEnabled: !observabilityDisabled,
          singleClientMode: useSingleClientMode,
          servicesMode,
          // Host and dedicated worker both use OPFS-backed SQLite.
          storage: { sqliteMode: defs.Runtime.Client.Storage.SqliteMode.OPFS },
        },
      },
    },
    config.values,
  );
  const services = await createClientServices(config, {
    createDedicatedWorker: () =>
      new Worker(new URL('./workers/dedicated-worker', import.meta.url), {
        type: 'module',
        name: 'dxos-client-worker',
      }),
    createCoordinatorWorker: () =>
      new SharedWorker(new URL('./workers/coordinator-worker', import.meta.url), {
        type: 'module',
        // Dev: SharedWorkers are keyed by (URL, name) and outlive vite restarts, so suffix the name
        // with the server boot id — a restarted server then gets a fresh coordinator instead of
        // attaching to a stale-code instance that new pages cannot negotiate with.
        name: `dxos-coordinator-worker${__DX_DEV_SERVER_BOOT_ID__ && `-${__DX_DEV_SERVER_BOOT_ID__}`}`,
      }),
    // TODO(wittjosiah): Instrument opfs worker?
    createOpfsWorker: () => new Worker(new URL('@dxos/client/opfs-worker', import.meta.url), { type: 'module' }),
    // Stale mixed-generation workers (tabs from before a dev-server restart) present as an endless
    // boot spinner with only a console warning; in dev, force every same-origin tab through one
    // coordinated reload so all generations converge. Production relies on the fatal dialog via the
    // startup timeout (tagged for telemetry — see ResetDialog).
    onPersistentWorkerFailure: (error) => {
      log.error('worker connection failing persistently', { error });
      if (import.meta.env?.DEV && !sessionStorage.getItem(DEV_RELOAD_GUARD_KEY)) {
        sessionStorage.setItem(DEV_RELOAD_GUARD_KEY, '1');
        new BroadcastChannel(DEV_RELOAD_CHANNEL).postMessage('reload');
        window.location.reload();
      }
    },
  });

  profiler?.mark('services:end');
  profiler?.measure('services', 'services:start', 'services:end');

  profiler?.mark('plugins:start');

  const isPwa = !isFalse(config.values.runtime?.app?.env?.DX_PWA);
  const conf: PluginConfig = {
    appKey: APP_KEY,
    config,
    services,
    observability,
    logStore,

    isDev: !['production', 'staging'].includes(config.values.runtime?.app?.env?.DX_ENVIRONMENT),
    isLocal: !isTauri && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'),
    isPwa,
    isTauri,
    isPopover,
    isMobile,
    isLabs: isTrue(config.values.runtime?.app?.env?.DX_LABS),
    isStrict: !isFalse(config.values.runtime?.app?.env?.DX_STRICT),
  };

  // `getPlugins` is synchronous: each plugin's main entry exposes only
  // `meta` + a `Plugin.lazy(...)` stub, so building the plugin array doesn't
  // pull any plugin's body. The plugin manager loads the real plugin
  // (separate Rollup chunk) on first `enable`. Remote plugin preload is
  // network-bound — the boot loader's counter follows that until the
  // plugin manager (post-React mount) takes over with module-activation
  // progress.
  bootStatus('Loading plugins…');
  const builtinPlugins = getPlugins(conf);
  const assetCache = await createAssetCache(isPwa, isTauri);
  const remotePluginsResult = await EffectEx.runPromise(
    UrlLoader.preload({
      cache: assetCache,
      onPluginLoaded: (loaded, total) => {
        // Pass `range` so the loader updates the existing line in place
        // ("Loading plugins (3/12)") instead of appending a fresh entry per
        // tick — keeps the visible log compact.
        bootLoader?.status({ humanized: 'Loading plugins', range: { index: loaded, total } });
        // The ring spans two phases — remote-plugin preload (0 → 50%) and
        // module activation (50 → 100%, driven from `Placeholder` once
        // React mounts). Splitting the range keeps it monotonic across
        // the boundary.
        bootLoader?.progress((loaded / total) * 0.5);
      },
    }),
  );

  bootStatus('Starting Composer…');
  // Park the ring at 50% — preload done, activation about to take over.
  bootLoader?.progress(0.5);
  const remotePlugins: Plugin.Plugin[] = remotePluginsResult;
  const plugins = [...builtinPlugins, ...remotePlugins];
  const pluginLoader = UrlLoader.make(builtinPlugins, { cache: assetCache });
  const onPluginRemove = (id: string) => UrlLoader.uninstall(id, { cache: assetCache });
  const defaults = getDefaults(conf);
  const setupEvents = [AppActivationEvents.SetupSettings];

  const edgeUrl = config.values.runtime?.services?.edge?.url;
  const pluginRegistryProvider = edgeUrl ? new EdgeRegistryPluginProvider(new EdgeHttpClient(edgeUrl)) : undefined;

  profiler?.mark('plugins:end');
  profiler?.measure('plugins-init', 'plugins:start', 'plugins:end');

  const Fallback = ({ error }: { error: Error }) => {
    const {
      needRefresh: [needRefresh],
      updateServiceWorker,
    } = useRegisterSW();

    const handleReset = useCallback(async () => {
      localStorage.clear();
      await services.services.SystemService?.reset();
      window.location.href = window.location.origin;
    }, [services]);

    return (
      // Double-fault guard: the themed dialog can itself fail to render (e.g. a
      // vite dev mid-optimization module split breaks the ThemeContext/i18n
      // identity), which would otherwise loop the outer 'app' boundary forever
      // instead of reporting the original error. Falls back to the
      // theme-independent ErrorFallback showing the startup error, and logs the
      // dialog's own failure with the `fatal_dialog` tag (ResetDialog's tagged
      // log never runs when its render crashes).
      <ErrorBoundary
        name='fatal-dialog'
        onError={(dialogError) =>
          log.error('fatal dialog failed to render', { error: dialogError, fatal_dialog: true })
        }
        fallbackRender={(props) => <ErrorFallback {...props} error={error} />}
      >
        <ThemeProvider tx={defaultTx} resourceExtensions={[...translations, ...observabilityTranslations]}>
          <Tooltip.Provider>
            <ResetDialog
              error={error}
              logStore={logStore}
              observability={observability}
              needRefresh={needRefresh}
              onRefresh={needRefresh ? () => void updateServiceWorker(true) : undefined}
              onReset={import.meta.env.DEV ? handleReset : undefined}
            />
          </Tooltip.Provider>
        </ThemeProvider>
      </ErrorBoundary>
    );
  };

  const Main = () => {
    const App = useApp({
      fallback: Fallback,
      // The boot loader (injected by `bootLoaderPlugin`, with the brand mark
      // supplied via `markSvg` in vite.config.ts) is the loading UI; `App`
      // relays startup progress into it and dismisses it — no placeholder here.
      pluginLoader,
      onPluginRemove,
      pluginRegistryProvider,
      plugins,
      defaults,
      setupEvents,
      cacheEnabled: true,
      safeMode,
      // The useLoading state machine ticks every `debounce` ms (Loading → FadeIn → FadeOut → Done),
      // so the gap between `Startup` activated and `<Placeholder>` dismissed is at least 2× debounce.
      // The boot loader covers the pre-React phase, so we don't need a longer fade to hide a flash.
      debounce: 200,
    });

    return <App />;
  };

  const root = document.getElementById('root')!;
  log('composer main: rendering App', { bootId: BOOT_ID, strict: conf.isStrict });
  if (conf.isStrict) {
    createRoot(root).render(
      <StrictMode>
        <Main />
      </StrictMode>,
    );
  } else {
    createRoot(root).render(<Main />);
  }
  // The boot loader is dismissed from `Placeholder.tsx`'s `useLayoutEffect` (gated on `stage >= 1`),
  // so it stays visible until React has actually committed its replacement.
};

void main();
